import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { fmtUSD, fmtPct, fmtDate } from '@/lib/calc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertCircle, RefreshCw, ArrowLeft, Plus, Trash2, FileText, Send, Check, X, Zap, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import AddLineDialog from '@/components/quotations/AddLineDialog';

const STATUS_STYLE = {
  Draft: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  InternalReview: 'bg-[hsl(60_30%_88%)] text-foreground border-[hsl(60_20%_78%)]',
  Sent: 'bg-[hsl(var(--soft-blush))] text-foreground border-[hsl(var(--soft-blush-dark))]',
  Approved: 'bg-[hsl(var(--muted-green))]/25 text-[hsl(var(--muted-green-dark))] border-[hsl(var(--muted-green))]/50',
  Declined: 'bg-foreground text-background border-foreground',
  Expired: 'bg-foreground/70 text-background border-foreground',
  Converted: 'bg-[hsl(var(--muted-green))] text-white border-[hsl(var(--muted-green-dark))]',
};

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const canCreate = ['admin','manager','sales'].includes(role);
  const canApprove = ['admin','manager'].includes(role);
  const showCosts = ['admin','manager','purchasing'].includes(role);

  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [missing, setMissing] = useState([]);

  const load = useCallback(async () => {
    setState('loading'); setError(null); setMissing([]);
    try {
      const d = await apiFetch(`/quotations/${id}`);
      setData(d);
      setEditValues({
        discount_amount: d.quotation.discount_amount ?? 0,
        discount_reason: d.quotation.discount_reason ?? '',
        delivery_fee: d.quotation.delivery_fee ?? 0,
        tax_rate: d.quotation.tax_rate ?? '',
        tax_override_reason: d.quotation.tax_override_reason ?? '',
        deposit_percent: d.quotation.deposit_percent ?? '',
        override_reason: d.quotation.override_reason ?? '',
      });
      setState('ok');
    } catch (e) { setError(e.message); setState('error'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, opts) => {
    setBusy(true);
    try {
      const r = await apiFetch(path, opts);
      await load();
      return r;
    } catch (e) {
      // If backend returned structured missing/margin object
      if (typeof e.data === 'object' && e.data?.missing) setMissing(e.data.missing);
      throw e;
    } finally { setBusy(false); }
  };

  const saveFinancials = async () => {
    const body = {
      discount_amount: Number(editValues.discount_amount || 0),
      discount_reason: editValues.discount_reason || null,
      delivery_fee: Number(editValues.delivery_fee || 0),
      deposit_percent: Number(editValues.deposit_percent || 0),
    };
    if (canApprove && editValues.tax_rate !== '' && editValues.tax_rate !== null) {
      body.tax_rate = Number(editValues.tax_rate);
      body.tax_override_reason = editValues.tax_override_reason || null;
    }
    if (canApprove && editValues.override_reason) body.override_reason = editValues.override_reason;
    try { await call(`/quotations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); toast.success('Saved'); }
    catch (e) { toast.error(e.message); }
  };

  const removeLine = async (lid) => {
    if (!window.confirm('Remove this line?')) return;
    try { await call(`/quotations/${id}/lines/${lid}`, { method: 'DELETE' }); toast.success('Line removed'); }
    catch (e) { toast.error(e.message); }
  };

  const doAction = async (action, reason) => {
    try {
      const r = await call(`/quotations/${id}/transition`, { method: 'POST', body: JSON.stringify({ action, reason }) });
      toast.success(`Moved to ${r.new_status}`);
    } catch (e) {
      toast.error(typeof e.data === 'object' ? (e.data.message || e.message) : e.message);
    }
  };

  const convert = async () => {
    try {
      const r = await call(`/quotations/${id}/convert`, { method: 'POST', body: '{}' });
      if (r.already_converted) toast.info(`Already converted to ${r.order_number}`);
      else toast.success(`Converted to order ${r.order_number}`);
    } catch (e) { toast.error(e.message); }
  };

  if (state === 'loading') return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-24 justify-center" data-testid="quote-detail-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading quotation…
    </div>
  );
  if (state === 'error') return (
    <div className="px-4 sm:px-6 lg:px-10 py-12">
      <div className="velvet-card p-8 text-center max-w-md mx-auto" data-testid="quote-detail-error">
        <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-3" />
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={load} className="rounded-sm" variant="outline" data-testid="quote-detail-retry">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
        </Button>
      </div>
    </div>
  );
  if (!data) return null;

  const { quotation: q, lines } = data;
  const editable = q.status === 'Draft' || q.status === 'InternalReview';

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1200px]" data-testid="quotation-detail-page">
      <button onClick={() => navigate('/quotations')} className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground velvet-eyebrow mb-6" data-testid="detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Quotations
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="velvet-eyebrow mb-2 flex items-center gap-3">
            <span className="font-mono normal-case tracking-normal">{q.number}</span>
            <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${STATUS_STYLE[q.status] || ''}`} data-testid={`detail-status`}>
              {q.status}
            </span>
          </div>
          <h1 className="velvet-page-title" data-testid="detail-customer-name">{q.customer_name || 'Unknown customer'}</h1>
          <div className="text-sm text-muted-foreground mt-2">{q.type} · {q.fulfillment} · Requested {q.requested_date ? fmtDate(q.requested_date) : '—'}</div>
          {q.valid_until && <div className="text-xs text-muted-foreground mt-1">Valid until {fmtDate(q.valid_until)}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/quotations/${id}/pdf`}><Button variant="outline" className="rounded-sm" data-testid="detail-pdf-btn"><FileText className="h-3.5 w-3.5 mr-1" /> PDF</Button></Link>
          {q.status === 'Draft' && canCreate && <Button disabled={busy} onClick={() => doAction('submit_review')} className="rounded-sm" variant="outline" data-testid="action-submit-review"><Send className="h-3.5 w-3.5 mr-1" /> Submit for review</Button>}
          {q.status === 'InternalReview' && canApprove && <Button disabled={busy} onClick={() => doAction('mark_sent')} className="rounded-sm" variant="outline" data-testid="action-mark-sent"><Send className="h-3.5 w-3.5 mr-1" /> Mark as sent</Button>}
          {q.status === 'InternalReview' && canCreate && <Button disabled={busy} onClick={() => doAction('return_to_draft')} className="rounded-sm" variant="outline" data-testid="action-return-draft">Return to draft</Button>}
          {q.status === 'Sent' && canApprove && (
            <>
              <Button disabled={busy} onClick={() => doAction('approve')} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="action-approve"><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>
              <Button disabled={busy} onClick={() => doAction('decline', prompt('Decline reason?') || 'Declined')} className="rounded-sm" variant="outline" data-testid="action-decline"><X className="h-3.5 w-3.5 mr-1" /> Decline</Button>
            </>
          )}
          {q.status === 'Approved' && canApprove && (
            !q.converted_order_id
              ? <Button disabled={busy} onClick={convert} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="action-convert"><Zap className="h-3.5 w-3.5 mr-1" /> Convert to order</Button>
              : <Link to="/orders"><Button variant="outline" className="rounded-sm" data-testid="action-view-order">View order</Button></Link>
          )}
        </div>
      </div>

      {missing.length > 0 && (
        <div className="border border-[hsl(var(--soft-blush-dark))] bg-[hsl(var(--soft-blush))]/50 p-4 mb-6 rounded-sm" data-testid="quote-missing-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-foreground" />
            <div>
              <div className="text-sm font-medium">Missing required values</div>
              <ul className="text-xs mt-1 list-disc list-inside text-muted-foreground">
                {missing.map((m, i) => <li key={i} data-testid={`missing-${i}`}>{m}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          <section className="velvet-card">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="velvet-section-title">Line items</h2>
              {editable && canCreate && (
                <Button size="sm" className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={() => setAddOpen(true)} data-testid="add-line-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-border">
                    <th className="px-5 py-2 velvet-eyebrow">Item</th>
                    <th className="px-5 py-2 velvet-eyebrow text-right w-16">Qty</th>
                    <th className="px-5 py-2 velvet-eyebrow text-right w-24">Price</th>
                    <th className="px-5 py-2 velvet-eyebrow text-right w-24">Line</th>
                    {showCosts && <th className="px-5 py-2 velvet-eyebrow text-right w-24">Cost</th>}
                    {editable && <th className="w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => (
                    <tr key={l.id} className="border-b border-border last:border-b-0" data-testid={`quote-line-${l.sku || l.item_name}`}>
                      <td className="px-5 py-3">
                        <div className="font-serif text-base">{l.item_name}</div>
                        <div className="text-xs text-muted-foreground flex gap-2 mt-0.5">
                          {l.sku && <span className="font-mono">{l.sku}</span>}
                          <span>{l.line_type === 'Custom' ? 'Custom arrangement' : 'Catalog'}</span>
                          {l.recipe_version_code && <span className="font-mono text-[10px]">v {l.recipe_version_code}</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right velvet-money">{l.qty}</td>
                      <td className="px-5 py-3 text-right velvet-money">{l.unit_price != null ? fmtUSD(l.unit_price) : <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Pending</span>}</td>
                      <td className="px-5 py-3 text-right velvet-money">{l.line_total != null ? fmtUSD(l.line_total) : '—'}</td>
                      {showCosts && <td className="px-5 py-3 text-right text-muted-foreground velvet-money">{l.line_cost != null ? fmtUSD(l.line_cost) : '—'}</td>}
                      {editable && (
                        <td className="px-2 py-3 text-right">
                          <button onClick={() => removeLine(l.id)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`remove-line-${l.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {lines.length === 0 && (
                    <tr><td colSpan={editable ? (showCosts ? 6 : 5) : (showCosts ? 5 : 4)} className="px-5 py-12 text-center text-muted-foreground" data-testid="no-lines">
                      No line items yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {q.card_message && (
            <section className="velvet-card p-5">
              <div className="velvet-eyebrow mb-2">Card message</div>
              <p className="text-sm font-serif italic">"{q.card_message}"</p>
            </section>
          )}
          {q.internal_notes && (
            <section className="velvet-card p-5" data-testid="internal-notes-section">
              <div className="velvet-eyebrow mb-2">Internal notes</div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{q.internal_notes}</p>
            </section>
          )}
        </div>

        <aside className="space-y-4">
          <section className="velvet-card p-5" data-testid="quote-totals">
            <div className="velvet-eyebrow mb-3">Totals</div>
            <Row label="Subtotal" v={q.subtotal} />
            {editable ? (
              <div className="py-2 border-b border-border/60">
                <label className="text-xs text-muted-foreground">Discount</label>
                <Input type="number" step="0.01" value={editValues.discount_amount} onChange={e => setEditValues(v => ({ ...v, discount_amount: e.target.value }))} className="rounded-sm h-8 text-right" data-testid="edit-discount" />
                <Input placeholder="Reason (required)" value={editValues.discount_reason} onChange={e => setEditValues(v => ({ ...v, discount_reason: e.target.value }))} className="rounded-sm h-8 mt-1 text-xs" data-testid="edit-discount-reason" />
              </div>
            ) : (
              <Row label="Discount" v={q.discount_amount} negative />
            )}
            {editable ? (
              <div className="py-2 border-b border-border/60">
                <label className="text-xs text-muted-foreground">Delivery fee</label>
                <Input type="number" step="0.01" value={editValues.delivery_fee} onChange={e => setEditValues(v => ({ ...v, delivery_fee: e.target.value }))} className="rounded-sm h-8 text-right" data-testid="edit-delivery" />
              </div>
            ) : (
              <Row label="Delivery" v={q.delivery_fee} />
            )}
            {editable && canApprove ? (
              <div className="py-2 border-b border-border/60">
                <label className="text-xs text-muted-foreground">Tax rate (0.0925 = 9.25%)</label>
                <Input type="number" step="0.0001" value={editValues.tax_rate} onChange={e => setEditValues(v => ({ ...v, tax_rate: e.target.value }))} className="rounded-sm h-8 text-right" data-testid="edit-tax-rate" />
                <Input placeholder="Override reason (required)" value={editValues.tax_override_reason} onChange={e => setEditValues(v => ({ ...v, tax_override_reason: e.target.value }))} className="rounded-sm h-8 mt-1 text-xs" data-testid="edit-tax-reason" />
              </div>
            ) : (
              <Row label={`Tax${q.tax_rate != null ? ` (${(q.tax_rate*100).toFixed(2)}%)` : ''}`} v={q.tax_amount} />
            )}
            <Row label="Total" v={q.total} bold />
            {editable ? (
              <div className="py-2 border-t border-border/60 mt-2">
                <label className="text-xs text-muted-foreground">Deposit %</label>
                <Input type="number" step="0.5" value={editValues.deposit_percent} onChange={e => setEditValues(v => ({ ...v, deposit_percent: e.target.value }))} className="rounded-sm h-8 text-right" data-testid="edit-deposit" />
              </div>
            ) : (
              <Row label={`Deposit${q.deposit_percent != null ? ` (${q.deposit_percent}%)` : ''}`} v={q.deposit_amount} />
            )}
            {editable && (
              <Button size="sm" onClick={saveFinancials} disabled={busy} className="rounded-sm bg-foreground text-background hover:bg-foreground/90 mt-3 w-full" data-testid="save-financials">Save financials</Button>
            )}
          </section>
          {showCosts && (
            <section className="velvet-card p-5" data-testid="internal-margin">
              <div className="velvet-eyebrow mb-3">Internal margin</div>
              <Row label="Total cost" v={q.total_cost} muted />
              <Row label="Gross profit" v={q.gross_profit} muted />
              <div className="text-sm flex justify-between py-2 border-t border-border/60 mt-2">
                <span className="text-muted-foreground">Gross margin</span>
                <span className="velvet-money" data-testid="gross-margin-value">{q.gross_margin != null ? fmtPct(q.gross_margin) : '—'}</span>
              </div>
              {editable && canApprove && (
                <div className="mt-3">
                  <label className="text-xs text-muted-foreground">Override reason (required if below minimum)</label>
                  <Textarea rows={2} value={editValues.override_reason} onChange={e => setEditValues(v => ({ ...v, override_reason: e.target.value }))} className="rounded-sm text-xs" data-testid="edit-override" />
                </div>
              )}
            </section>
          )}
          {q.converted_order_id && (
            <section className="velvet-card p-4 text-center" data-testid="converted-info">
              <div className="velvet-eyebrow mb-1">Converted</div>
              <Link to="/orders" className="text-sm font-mono hover:underline">Order in production</Link>
            </section>
          )}
        </aside>
      </div>

      <AddLineDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={load}
        quoteId={id}
      />
    </div>
  );
}

function Row({ label, v, bold, muted, negative }) {
  const isNull = v == null;
  return (
    <div className={`text-sm flex justify-between py-2 border-b border-border/60 ${bold ? 'font-medium border-t border-foreground/30 mt-1 pt-3' : ''}`}>
      <span className={muted ? 'text-muted-foreground' : ''}>{label}</span>
      <span className={`velvet-money ${muted ? 'text-muted-foreground' : ''}`}>
        {isNull ? <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Pending</span> : `${negative && Number(v) > 0 ? '- ' : ''}${fmtUSD(v)}`}
      </span>
    </div>
  );
}
